import { useQuery } from '@apollo/client';
import { BoltSlashIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { InfoOutlined } from '@mui/icons-material';
import { gql } from '__generated__/gql';
import { DealTypeEnumType, IndustryTypeEnumType } from '__generated__/graphql';
import classNames from 'classnames';
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ErrorMessage from 'components/ErrorMessage';

import Button from 'primitives/Button';
import { Dialog, DialogPanel, DialogTitle } from 'primitives/Dialog';
import LoadingIndicator from 'primitives/LoadingIndicator';
import Select from 'primitives/Select';

import industryToReadable from 'utils/enums/industry-to-readable';

import { useInvestPageNav } from '../InvestPage/InvestPageNavContext';
import ExploreDealCard, { ExploreDealData } from './ExploreDealCard';

const INVESTMENT_ENTITIES_QUERY_EXPLORE_DEALS = gql(`
    query FetchInvestmentEntitiesForExploreDeals {
      dashboard {
        id
        investmentEntities {
          id
          name
          status
        }
      }
    }
  `);

const DEALS_QUERY_EXPLORE_DEALS = gql(`
        query FetchDealsForExploreDeals($investmentEntityId: ID!) {
            exploreDeals(investmentEntityId: $investmentEntityId) {
                nodes {
                    id
                    type
                    companyName
                    companyDescription
                    companyImage
                    companyWebsite
                    lastDate
                    isAccredited
                    isRequested
                    dealInviteLink
                    industries
                    round
                    minInvestmentAmount
                    financialInstrument
                    valuationType
                    valuationOrCap
                    capPrice
                    floorPrice
                    customEngagementRules
                    externalInvestors {
                        id
                        name
                        isLead
                    }
                }
            }
        }
    `);

/** Each Invest tab renders this page scoped to one vehicle. */
export type DiscoverDealVehicle = DealTypeEnumType.Syndicated | DealTypeEnumType.Ruv;

const VEHICLE_COPY: Record<DiscoverDealVehicle, { noun: string; subTitle: string }> = {
  [DealTypeEnumType.Syndicated]: {
    noun: 'syndicated deals',
    subTitle: 'Browse active syndicated deals',
  },
  [DealTypeEnumType.Ruv]: {
    noun: 'RUVs',
    subTitle: 'Browse active roll-up vehicles',
  },
};

function filterDealsByType(
  dealList: ExploreDealData[],
  dealType: DiscoverDealVehicle
): ExploreDealData[] {
  return dealList.filter(deal => deal.type === dealType);
}

/** When empty, no industry filter. Otherwise include deals that list at least one selected industry. */
function filterDealsBySelectedIndustries(
  dealList: ExploreDealData[],
  selectedIndustryValues: string[]
): ExploreDealData[] {
  if (selectedIndustryValues.length === 0) return dealList;
  const selected = new Set(selectedIndustryValues);
  return dealList.filter(deal => {
    const industries = deal.industries;
    if (!industries?.length) return false;
    return industries.some(i => selected.has(i));
  });
}

const ExploreDeals: FC<{ dealType: DiscoverDealVehicle }> = ({ dealType }) => {
  const [investmentEntityId, setInvestmentEntityId] = useState<string | undefined>(undefined);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const navigate = useNavigate();
  const { setNavMeta, setDealTypeCounts } = useInvestPageNav();

  const { loading, error, data, refetch } = useQuery(INVESTMENT_ENTITIES_QUERY_EXPLORE_DEALS);

  const {
    loading: dealsLoading,
    error: dealsError,
    data: dealsData,
    refetch: dealsRefetch,
  } = useQuery(DEALS_QUERY_EXPLORE_DEALS, {
    variables: {
      investmentEntityId: investmentEntityId || '',
    },
    skip: !investmentEntityId,
  });

  const investmentEntities = (data?.dashboard?.investmentEntities ?? []).filter(
    ie => ie.status === 'ACTIVE'
  );

  useEffect(() => {
    if (investmentEntities.length && !investmentEntityId) {
      const defaultEntity = investmentEntities.find(entity => entity.status === 'ACTIVE')?.id;
      if (defaultEntity) setInvestmentEntityId(defaultEntity);
    }
  }, [data, investmentEntityId]);

  useEffect(() => {
    setSelectedIndustries([]);
  }, [investmentEntityId, dealType]);

  useEffect(() => {
    if (!investmentEntities.length) return;
    setNavMeta({
      subTitle: VEHICLE_COPY[dealType].subTitle,
      navRightContent: (
        <Select
          options={investmentEntities.map(ie => ({
            label: ie.name,
            value: ie.id,
            disabled: ie.status !== 'ACTIVE',
            labelSecondaryText: ie.status === 'ACTIVE' ? undefined : 'Inactive',
          }))}
          onChange={value => setInvestmentEntityId(value)}
          value={investmentEntityId}
          className="w-52"
        />
      ),
    });
  }, [investmentEntities, investmentEntityId, dealType, setNavMeta]);

  const dealNodes = dealsData?.exploreDeals?.nodes;

  // Both tabs come out of the same query, so this page owns the counts for the nav.
  const dealTypeCounts = useMemo(() => {
    const list = (dealNodes ?? []) as ExploreDealData[];
    return {
      [DealTypeEnumType.Syndicated]: filterDealsByType(list, DealTypeEnumType.Syndicated).length,
      [DealTypeEnumType.Ruv]: filterDealsByType(list, DealTypeEnumType.Ruv).length,
    };
  }, [dealNodes]);

  useEffect(() => {
    if (!dealNodes) return;
    setDealTypeCounts(dealTypeCounts);
  }, [dealNodes, dealTypeCounts, setDealTypeCounts]);

  const dealsForVehicle = useMemo(() => {
    return filterDealsByType((dealNodes ?? []) as ExploreDealData[], dealType);
  }, [dealNodes, dealType]);

  const allIndustriesInDeals = useMemo(() => {
    return dealsForVehicle
      ?.map(deal => deal?.industries?.flat())
      .flat()
      .map(industry => {
        return {
          value: industry as IndustryTypeEnumType,
          label: industryToReadable(industry as IndustryTypeEnumType).label,
        };
      })
      .filter((_, index, self) => index === self.findIndex(t => t.value === _.value));
  }, [dealsForVehicle]);

  const filteredDeals = useMemo(() => {
    return filterDealsBySelectedIndustries(dealsForVehicle, selectedIndustries);
  }, [dealsForVehicle, selectedIndustries]);

  const toggleIndustryFilter = (value: string) => {
    setSelectedIndustries(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  const EXPLORE_CONSENT_SESSION_KEY = 'exploreConsentAcknowledged';

  useEffect(() => {
    const accredited = dealNodes?.length ? dealNodes.every(deal => deal?.isAccredited) : false;
    if (accredited && !sessionStorage.getItem(EXPLORE_CONSENT_SESSION_KEY)) {
      setShowConsentDialog(true);
    }
  }, [dealNodes]);

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const isAccredited = dealNodes?.every(deal => deal?.isAccredited);

  if (investmentEntities.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-blue-500 flex justify-center items-center mb-4">
          <BoltSlashIcon className="w-10 h-10" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No investment entities available</h3>
        <p className="text-gray-600">Please contact support to add an investment entity.</p>
      </div>
    );
  }

  if (dealsLoading) {
    return <LoadingIndicator />;
  }

  if (dealsError) {
    return <ErrorMessage error={dealsError} refetch={dealsRefetch} />;
  }

  return (
    <div className="space-y-6">
      {showConsentDialog ? (
        <Dialog
          open={showConsentDialog}
          zIndexPriority="high"
          onClose={() => {
            setShowConsentDialog(false);
            sessionStorage.setItem(EXPLORE_CONSENT_SESSION_KEY, 'true');
          }}
        >
          <DialogPanel className="w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white p-0 text-left shadow-2xl ring-1 ring-slate-900/5">
            <div className="bg-gradient-to-br from-indigo-50 via-white to-slate-50 px-6 pb-5 pt-6">
              <DialogTitle>Eligibility and Confidentiality</DialogTitle>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">
                Please acknowledge the following declarations before accessing investment
                opportunities and related information.
              </p>
            </div>
            <div className="space-y-3 px-6 py-5 max-h-[min(380px,55vh)] overflow-y-auto">
              {[
                `I/ We attest that I/ we qualify as an eligible 'Angel Investor' as per the SEBI (AIF) Regulations, 2012, and are competent to invest in the securities market as per applicable laws.`,
                `I/We shall maintain confidentiality and not disclose any information in connection with the Offer to any third party, except to its professional advisers on a need-to-know basis and subject to equivalent confidentiality obligations.`,
                `I/We shall act in good faith in relation to the Offer, and shall not engage in any conduct that is misleading or prejudicial to the Fund, the Investment Manager, or other Eligible Investors.`,
                `I/We confirm that all information, representations, and confirmations provided by me/us in connection with the Offer are true, complete, and accurate.`,
              ].map((condition, index) => (
                <div key={condition} className="flex gap-3 rounded-xl bg-slate-50 p-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700">
                    {index + 1}
                  </span>
                  <p className="text-sm leading-relaxed text-slate-600">{condition}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-100 bg-slate-50/80 px-6 py-4">
              <Button
                variant="primary"
                className="w-full py-2.5 text-sm font-semibold"
                onClick={() => {
                  setShowConsentDialog(false);
                  sessionStorage.setItem(EXPLORE_CONSENT_SESSION_KEY, 'true');
                }}
              >
                I acknowledge and continue
              </Button>
            </div>
          </DialogPanel>
        </Dialog>
      ) : null}

      {!dealNodes?.length ? (
        <div className="text-center py-12">
          <div className="text-indigo-600 flex justify-center items-center mb-4">
            <InfoOutlined className="w-16 h-16" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No deals available</h3>
          <p className="text-gray-600">
            We will recommend new investment opportunities as soon as fresh deals open up.
          </p>
        </div>
      ) : (
        <>
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-slate-900">Discover Opportunities</h2>
              {allIndustriesInDeals && allIndustriesInDeals.length > 0 && (
                <div className="-mx-1 mt-4 overflow-x-auto px-1 pb-1">
                  <div className="flex w-max items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedIndustries([])}
                      className={classNames(
                        'shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
                        selectedIndustries.length === 0
                          ? 'bg-indigo-600 text-white shadow-sm hover:bg-indigo-700'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      )}
                    >
                      All
                    </button>
                    {allIndustriesInDeals.map(industry => {
                      const isSelected = selectedIndustries.includes(industry.value);
                      return (
                        <button
                          key={industry.value}
                          type="button"
                          onClick={() => toggleIndustryFilter(industry.value)}
                          className={classNames(
                            'shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
                            isSelected
                              ? 'bg-indigo-600 text-white shadow-sm hover:bg-indigo-700'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          )}
                        >
                          {industry.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            {dealsForVehicle.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 py-14 text-center">
                <p className="text-base font-medium text-slate-800">
                  No {VEHICLE_COPY[dealType].noun} open right now
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  We will recommend new opportunities as soon as fresh ones open up.
                </p>
              </div>
            ) : filteredDeals.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 py-14 text-center">
                <p className="text-base font-medium text-slate-800">No deals match your filters</p>
                <p className="mt-1 text-sm text-slate-500">
                  Try clearing industries or pick a different combination.
                </p>
                <button
                  type="button"
                  className="mt-4 text-sm font-semibold text-indigo-600 hover:text-indigo-800"
                  onClick={() => setSelectedIndustries([])}
                >
                  Clear industry filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {filteredDeals.map(deal => (
                  <ExploreDealCard
                    key={deal.id}
                    {...deal}
                    investmentEntityId={investmentEntityId ?? ''}
                    refetch={dealsRefetch}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
      {!isAccredited ? (
        <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6 mt-12 text-center space-y-6">
          <div className="space-y-2">
            <div className="mx-auto w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <LockClosedIcon className="h-6 w-6 text-slate-600" />
            </div>
            <h3 className="text-2xl font-semibold text-slate-900">Unlock Access to All Deals</h3>
            <p className="text-slate-500 max-w-xl mx-auto">
              These investment opportunities are available only to accredited investors. Complete
              your accreditation process to view full details and start investing.
            </p>
          </div>
          <Button
            variant="primary"
            className="w-full sm:w-auto px-8 py-3 mx-auto"
            onClick={() => navigate('/invest/accreditation')}
          >
            Unlock Access
          </Button>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default ExploreDeals;
